import { useImperativeHandle, useRef, type Ref } from 'react';
import {
  CheckIcon,
  ChevronDownIcon,
  PlayIcon,
  RotateCcwIcon,
  SquareIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { TaskView } from '@/lib/api/generated/model';
import type { BoardStepSection } from '../boardModel';
import { useTaskCommands } from '../hooks/useTaskCommands';
import {
  PENDING_LABEL,
  usePendingTaskAction,
} from '../hooks/usePendingTaskActions';
import {
  derivePrimaryAction,
  type Outcome,
  type PrimaryAction,
  type TaskChoice,
} from '../primaryAction';
import { useCanWork } from '@/features/workspaces/canWork';

/** What a keyboard shortcut or the palette can ask of this control. */
export interface TaskPrimaryActionHandle {
  /**
   * Do what pressing the primary control would do. With several outcomes
   * nothing is chosen on the person's behalf: focus moves to the first, so
   * the choice is still theirs and one key away.
   */
  activate: () => void;
}

export interface TaskPrimaryActionProps {
  workspaceId: string;
  task: TaskView;
  step: BoardStepSection | undefined;
  steps: readonly BoardStepSection[];
  /** `header`: labelled buttons in the drawer; `compact`: on a card or a row. */
  density: 'header' | 'compact';
  /** Called after an accepted action — the Overview row moves focus on. */
  onDone?: () => void;
  /**
   * Whether the leading button wears the primary style. Your turn shows one
   * primary action at a time, on the row the person is on (spec 152 FR-008),
   * and passes `quiet` to every other row. Defaults to `primary`, as before.
   */
  emphasis?: 'primary' | 'quiet';
  /** Computed by the caller when it already has it (the card does). */
  action?: PrimaryAction;
  /**
   * Report a decision or Run again instead of doing it (spec 154 R2): the
   * Overview's Your turn holds it for 6 seconds first. Start and Stop never
   * appear there, so they always act at once.
   */
  onChoose?: (choice: TaskChoice) => void;
  className?: string;
  ref?: Ref<TaskPrimaryActionHandle>;
}

/**
 * The one thing to do next with a task, as a control (spec 125 FR-002 –
 * FR-006). Renders nothing when there is nothing to do.
 */
function TaskPrimaryActionControl({
  workspaceId,
  task,
  step,
  steps,
  density,
  onDone,
  emphasis = 'primary',
  action: given,
  onChoose,
  className,
  ref,
}: TaskPrimaryActionProps) {
  const action = given ?? derivePrimaryAction(task, step, steps);
  const commands = useTaskCommands(workspaceId);
  const pending = usePendingTaskAction(task.id);
  const firstRef = useRef<HTMLButtonElement>(null);
  const size = density === 'header' ? 'sm' : 'xs';
  const lead = emphasis === 'quiet' ? 'outline' : 'default';
  const busy = pending !== undefined;
  const labelOf = (stepId: string) =>
    steps.find((s) => s.stepId === stepId)?.label ?? stepId;
  /*
    On a row or a card several tasks offer the same verb side by side, so each
    button is named for its task as well, "Done: Search results" (spec 151
    FR-006). The name starts with exactly what the button shows, pending text
    included, so speech input matches what is on screen. The drawer shows one
    task and keeps its plain names.
  */
  const named = (shown: string) =>
    density === 'compact' ? `${shown}: ${task.title}` : undefined;

  const decide = (outcome: Outcome) => {
    const targetLabel = labelOf(outcome.to);
    if (onChoose) onChoose({ kind: 'decide', outcome, targetLabel });
    else commands.decide(task, outcome, targetLabel, onDone);
  };

  const run = () => {
    switch (action.kind) {
      case 'start':
        commands.start(task, action.target, onDone);
        return;
      case 'stop':
        commands.stop(task);
        return;
      case 'run-again':
        if (onChoose) onChoose({ kind: 'run-again' });
        else commands.runAgain(task);
        return;
      case 'decide': {
        const [only] = action.outcomes;
        if (action.outcomes.length === 1 && only) {
          decide(only);
        } else {
          firstRef.current?.focus();
        }
        return;
      }
      case 'none':
        return;
    }
  };

  useImperativeHandle(ref, () => ({ activate: run }));

  if (action.kind === 'none') return null;

  const pendingText = pending ? PENDING_LABEL[pending] : null;
  const wrap = cn('flex flex-wrap items-center gap-1', className);

  if (action.kind === 'start') {
    return (
      <div className={wrap}>
        <Button
          ref={firstRef}
          type="button"
          size={size}
          variant={lead}
          disabled={busy}
          aria-label={
            density === 'compact'
              ? `Start ${task.title} at ${action.target.label}`
              : undefined
          }
          onClick={run}
        >
          <PlayIcon aria-hidden="true" strokeWidth={1.5} />
          {pendingText ??
            (density === 'header' ? `Start: ${action.target.label}` : 'Start')}
        </Button>
        {density === 'header' && action.others.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  type="button"
                  size="icon-sm"
                  variant="outline"
                  disabled={busy}
                  aria-label="Start at another step"
                />
              }
            >
              <ChevronDownIcon aria-hidden="true" strokeWidth={1.5} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-52">
              {/* Base UI's group label must sit inside a group. */}
              <DropdownMenuGroup>
                <DropdownMenuLabel>Start at</DropdownMenuLabel>
                {action.others.map((other) => (
                  <DropdownMenuItem
                    key={other.stepId}
                    onClick={() => {
                      commands.start(task, other, onDone);
                    }}
                  >
                    {other.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    );
  }

  if (action.kind === 'decide') {
    const shown = action.outcomes.slice(0, 2);
    const more = action.outcomes.slice(2);
    return (
      <div className={wrap} role="group" aria-label={`Decide ${task.title}`}>
        {shown.map((outcome, index) => (
          <Button
            key={outcome.key}
            ref={index === 0 ? firstRef : undefined}
            type="button"
            size={size}
            variant={index === 0 ? lead : 'outline'}
            disabled={busy}
            aria-label={named(
              index === 0 && pendingText ? pendingText : outcome.label,
            )}
            onClick={() => {
              decide(outcome);
            }}
          >
            {index === 0 ? (
              <CheckIcon aria-hidden="true" strokeWidth={1.5} />
            ) : null}
            {index === 0 && pendingText ? pendingText : outcome.label}
          </Button>
        ))}
        {more.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  type="button"
                  size={density === 'header' ? 'icon-sm' : 'icon-xs'}
                  variant="outline"
                  disabled={busy}
                  aria-label={
                    density === 'compact'
                      ? `More outcomes for ${task.title}`
                      : 'More outcomes'
                  }
                />
              }
            >
              <ChevronDownIcon aria-hidden="true" strokeWidth={1.5} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-52">
              {more.map((outcome) => (
                <DropdownMenuItem
                  key={outcome.key}
                  onClick={() => {
                    decide(outcome);
                  }}
                >
                  {outcome.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    );
  }

  const Icon = action.kind === 'stop' ? SquareIcon : RotateCcwIcon;
  const shownText =
    pendingText ?? (action.kind === 'stop' ? 'Stop' : 'Run again');
  return (
    <div className={wrap}>
      <Button
        ref={firstRef}
        type="button"
        size={size}
        variant={action.kind === 'stop' ? 'outline' : lead}
        disabled={busy}
        aria-label={named(shownText)}
        onClick={run}
      >
        <Icon aria-hidden="true" strokeWidth={1.5} />
        {shownText}
      </Button>
    </div>
  );
}

/**
 * Offered only to someone who may work in the workspace (spec 189): a person reading it gets nothing that would only be refused.
 */
export function TaskPrimaryAction(props: TaskPrimaryActionProps) {
  return useCanWork() ? <TaskPrimaryActionControl {...props} /> : null;
}
